import React, { useCallback, useMemo, useReducer } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Progress } from '../../../../components/ui/progress';
import { TreeNode as TreeNodeType } from '../../models/TreeModel';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { usePinnedObjectsStore } from '../../../database-objects/store/pinnedObjectsStore';
import { canonicalNodeTypeForPin } from '../../models/nodeFactories';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { PinnedObjectRef } from '../../../../types/electron';
import {
  computeReorderedRefs,
  applyPinnedReorder,
} from '../../../database-objects/services/pinnedObjects/pinnedReorderHandler';
import {
  groupPinnedChildrenByDatabase,
  type PinnedAreaRun,
} from '../../../database-objects/services/pinnedObjects/groupPinnedChildrenByDatabase';
import { PinnedAreaShell } from './PinnedAreaShell';
import { createRefreshAction, generateActionsForNodeType } from '../../services/contextMenuService';
import { generateTableContextMenuActions } from '../../../database-objects/services/tableContextMenuService';
import { generateColumnContextMenuActions } from '../../../database-objects/services/columnContextMenuService';
import { generateViewContextMenuActions } from '../../../database-objects/services/viewContextMenuService';
import { generateDatabaseContextMenuActions } from '../../../database-objects/services/databaseContextMenuService';
import { generateSchemaContextMenuActions } from '../../../database-objects/services/schemaContextMenuService';
import { generateRoutineContextMenuActions } from '../../../database-objects/services/routineContextMenuService';
import { generateConnectionContextMenuActions } from '../../../database-objects/services/connectionContextMenuService';
import { generateStorageContextMenuActions, type StorageMenuNode } from '../../../database-objects/services/storageContextMenuService';
import { isStorageNodeType, isStorageResourceNodeType } from '../../models/azureStorageNodeFactories';
import { openStorageExplorerTab } from '../../../workspace/storage-explorer/utils/openStorageExplorerTab';
import {
  isSecurityObjectType,
  generateSecurityObjectContextMenuActions,
} from '../../../database-objects/services/securityContextMenuService';
import { generateKeyContextMenuActions } from '../../../database-objects/services/keyContextMenuService';
import { generateConstraintContextMenuActions } from '../../../database-objects/services/constraintContextMenuService';
import { generateIndexContextMenuActions } from '../../../database-objects/services/indexContextMenuService';
import { generateTriggerContextMenuActions } from '../../../database-objects/services/triggerContextMenuService';
import { generateStatisticContextMenuActions } from '../../../database-objects/services/statisticContextMenuService';
import { generateDirectoryContextMenuActions } from '../../../database-objects/services/directoryContextMenuService';
import { generatePartitionContextMenuActions } from '../../../database-objects/services/partitionContextMenuService';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { createDatabaseNodeId } from '../../utils/nodeIdUtils';
import { VQE_DRAG_TABLE_MIME } from '../../../workspace/visual-query-editor/render/common/dragSchemaPayload';
import { extractDatabaseFromParentId } from '../../../database-objects/services/tableContextMenuService';
import {
  generatePinnedWorkspaceContextMenuActions,
  getUpdateFromTabAvailability,
} from '../../../database-objects/services/pinnedObjects/pinnedWorkspaceContextMenuService';
import type { KeyNodeMetadata, ConstraintNodeMetadata, TriggerNodeMetadata, PinnedWorkspaceNode } from '../../models/nodeTypes';
import type { DatabaseObjectNodeMetadata } from '../../models/DatabaseTreeModels';
import type { DatabaseEngineId } from '@/core/engines';
import { useHasProAccess } from '@/core/licensing/hooks/useHasProAccess';
import { useDashboardStore } from '@/features/workspace/dashboards/store/dashboardStore';
import { FREE_DASHBOARD_LIMIT } from '@/features/workspace/dashboards/types/dashboardTypes';
import { generateDashboardContextMenuActions } from '@/features/workspace/dashboards/explorer/dashboardExplorerContextMenu';
import { openDashboardFromExplorer } from '@/features/workspace/dashboards/explorer/dashboardExplorerActions';
import { TreeNodeContent, type TreeNodeSortableProps } from './TreeNodeContent';
import { TreeNodeLoading } from './TreeNodeLoading';
import { TreeNodeContextMenu } from './TreeNodeContextMenu';
import { TreeNodeSkeleton } from './TreeNodeSkeleton';
import { useTreeContext } from '../../context/TreeContext';
import { useTreeSelection } from '../../context/treeSelectionStore';

interface TreeNodeProps {
  node: TreeNodeType;
  depth: number;
  /**
   * When this row participates in a parent `SortableContext` (pinned siblings
   * under a connection), the wrapper passes drag wiring down. The row applies
   * the sortable ref/style and shows a hover-only grip handle.
   */
  sortable?: TreeNodeSortableProps;
  /**
   * True when this node is rendered inside the tinted PinnedAreaShell. Keeps
   * the node's wrappers (and its whole descendant subtree) transparent so the
   * shell tint shows through expanded children too — not just the pinned row.
   */
  insidePinnedArea?: boolean;
  /**
   * The owning connection's color (hex). At the connection root the value is
   * looked up from the connection store; descendants receive it via this prop
   * and forward it down so every row in the subtree paints the same left-edge
   * bar.
   */
  connectionColor?: string;
}

function TreeNodeImpl({ node, depth, sortable, insidePinnedArea, connectionColor }: TreeNodeProps) {
  // Perf gate instrumentation (same `__SQL_STUDIO_PROFILE__` opt-in as
  // `QueryEditorTab`). The Object Explorer tree is not virtualized, so "how
  // many rows did that interaction re-render" is the number that actually
  // predicts felt latency — `objectExplorerContextMenu.perf.spec.ts` asserts
  // on it. A plain counter rather than a log line: with ~1,000 rows, logging
  // per row would itself dominate the measurement.
  if (typeof window !== 'undefined' && (window as unknown as { __SQL_STUDIO_PROFILE__?: boolean }).__SQL_STUDIO_PROFILE__) {
    const w = window as unknown as { __SQL_STUDIO_TREE_NODE_RENDERS__?: number };
    w.__SQL_STUDIO_TREE_NODE_RENDERS__ = (w.__SQL_STUDIO_TREE_NODE_RENDERS__ ?? 0) + 1;
  }
  const {
    selectionStore,
    onNodeExpand,
    onNodeSelect,
    onNodeRefresh,
    onContextMenuAction,
    onNodeHighlightComplete,
    onHighlightedNodeComplete,
    onReconnect,
    iconMap,
    aiCliAvailable,
    aiCliDetectionLoading,
  } = useTreeContext();

  // Perf — each row subscribes to a boolean scoped to its OWN id rather than
  // reading the whole selection off `TreeContext`. A selection change then
  // re-renders exactly the two rows whose selected-ness flipped instead of
  // every row in the tree (see `treeSelectionStore.ts`).
  const isSelected = useTreeSelection(selectionStore, (s) => s.selectedNodeId === node.id);

  // A1 (Codex final-gate) — a `pinned-workspace` row's "Update from open tab"
  // availability is recomputed from a fresh `useWorkspaceStore` snapshot at
  // render time (see the `pinned-workspace` context-menu branch below). The
  // freshness normally rides on the right-click selecting the row (which
  // re-renders), but when the row is ALREADY selected — e.g. left selected by
  // double-click-to-open — no selection change fires and the menu would show
  // stale availability. Force a re-render when the menu opens so availability
  // is recomputed just before it renders.
  const [, forceContextMenuRefresh] = useReducer((x: number) => x + 1, 0);

  // Check if this connection node should be highlighted (for new connections)
  const connectionId = node.type === 'connection' ? (node.metadata as { id: string })?.id : null;
  const isConnectionHighlighted = useTreeSelection(selectionStore, (s) =>
    connectionId ? s.highlightedConnectionIds?.has(connectionId) ?? false : false,
  );

  // Resolve the owning connection's color at the root, then forward it down.
  // Descendants receive the resolved color via the `connectionColor` prop and
  // never re-read the store themselves — this keeps the subtree consistent
  // mid-render and avoids one store subscription per row.
  const ownColor = useConnectionStore((s) =>
    connectionId ? s.connections.find((c) => c.id === connectionId)?.color : undefined,
  );
  const effectiveConnectionColor = connectionId ? ownColor : connectionColor;

  // S23 — subscribed unconditionally (rules-of-hooks; every row already does
  // this for `useConnectionStore` above) but only read inside the 'dashboard'
  // branch below, to compute the free-tier "Duplicate" blocked state.
  const dashboardCount = useDashboardStore((s) => s.dashboards.length);
  const hasProAccess = useHasProAccess();

  // Check if this specific node should be highlighted (for toolbar context sync)
  const isNodeHighlighted = useTreeSelection(selectionStore, (s) => s.highlightedNodeId === node.id);
  const highlightedConnectionCount = useTreeSelection(
    selectionStore,
    (s) => s.highlightedConnectionIds?.size ?? 0,
  );

  // Combined highlight state
  const isHighlighted = isConnectionHighlighted || isNodeHighlighted;

  // Only scroll into view when this is the sole highlighted connection. During
  // session restore many connections come online in a burst — letting each one
  // call scrollIntoView causes a stuttery cascade of competing smooth scrolls.
  const isOnlyConnectionHighlight = isConnectionHighlighted && highlightedConnectionCount <= 1;
  const shouldScrollIntoView = isNodeHighlighted || isOnlyConnectionHighlight;

  // 2026-05-26 — pinned wrappers are now full drill-in nodes (the user
  // overrode the original "shortcut-only" decision). They get a chevron when
  // `canHaveChildren` is true OR their type is folder-shaped — same rule as
  // canonical rows. The downstream expansion handler has explicit branches
  // for `pinned-database`, `pinned-folder`, and `pinned-object` so the
  // chevron click is no longer a no-op.
  const showExpander =
    node.canHaveChildren ||
    (node.children && node.children.length > 0) ||
    node.type.includes('-folder');

  const isExpanded = node.isExpanded;
  // Check for loading state in multiple places (root level isLoading or metadata.isLoading)
  const nodeWithLoading = node as TreeNodeType & { isLoading?: boolean };
  const isLoading = node.type === 'loading' || nodeWithLoading.isLoading || node.metadata?.isLoading;
  // Check if children are loading (has a loading/message child that indicates data is being fetched)
  const hasLoadingChildren = node.children?.some(
    child => child.type === 'loading' || (child.type === 'message' && child.icon === 'loading')
  ) ?? false;

  const nodeWithActions = { ...node };

  // `aiCliAvailable` / `aiCliDetectionLoading` (from `useTreeContext()` above)
  // drive the "AI" submenu's disabled state on table/view rows below. T12 fix
  // round 1, M-4 — this used to be a per-row `useCliDetection()` call (one IPC
  // probe + one permanent window listener per row on a cold cache, 200
  // rows -> 200 calls). Now it's a single read from `DatabaseExplorer.tsx`,
  // threaded through `TreeContext` — one subscription for the whole tree.

  // F4 — For pinned wrappers, drive context-menu generation off the CANONICAL
  // underlying type (database / tables-folder / table / view / stored-procedure /
  // function) so users get the full set of scripting / select-top / refresh
  // actions on a pinned row instead of the generic `Copy name` + `Refresh`
  // fallback. The pinned wrapper's pinRef carries the canonical identity.
  const effectiveType: string = canonicalNodeTypeForPin(
    node as unknown as { type: string; pinRef?: import('../../../../types/electron').PinnedObjectRef },
  ) ?? node.type;

  if (isStorageNodeType(node.type) || node.type === 'pinned-storage') {
    // Azure Storage (spec §5.2) — its own menu; never the SQL folder / object menus.
    nodeWithActions.contextMenuActions = generateStorageContextMenuActions(node as unknown as StorageMenuNode);
  } else if (effectiveType === 'table') {
    // Resolve the owning engine so kusto tables get a read-only menu (no DDL/DML,
    // Select-top emits KQL), and pinned Oracle/PG tables get their engine-specific
    // items. Handles both canonical (`objectDetails.connectionId`) and
    // pinned-wrapper (`connectionId`) metadata shapes — same as the routine
    // branch below.
    const tableMeta = node.metadata as
      (DatabaseObjectNodeMetadata & { connectionId?: string })
      | undefined;
    const tableConnectionId = tableMeta?.connectionId ?? tableMeta?.objectDetails?.connectionId;
    const tableEngine = tableConnectionId
      ? getEngineForConnection(tableConnectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateTableContextMenuActions(
      tableEngine as DatabaseEngineId | undefined,
      aiCliAvailable,
      aiCliDetectionLoading,
    );
  } else if (effectiveType === 'column') {
    // Resolve the owning engine so kusto columns don't get a "Find usages…"
    // entry backed by T-SQL — same shape as the table branch above.
    const columnMeta = node.metadata as { connectionId?: string } | undefined;
    const columnEngine = columnMeta?.connectionId
      ? getEngineForConnection(columnMeta.connectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateColumnContextMenuActions(columnEngine);
  } else if (effectiveType === 'view') {
    nodeWithActions.contextMenuActions = generateViewContextMenuActions(
      aiCliAvailable,
      aiCliDetectionLoading,
    );
  } else if (effectiveType === 'stored-procedure' || effectiveType === 'function') {
    // Review finding #5 — for `pinned-object` whose canonical type is a routine,
    // metadata is the pinned-wrapper shape (`{ connectionId, database, engine,
    // schema, name }`) with NO `objectDetails`. Falling through to
    // `objectDetails?.connectionId` left the engine undefined for every pinned
    // Oracle stored proc / function, silently dropping Oracle-only items
    // ("Open", "Compile for Debug"). Check the wrapper shape first so pinned
    // routines see the same menu as canonical ones.
    const routineMeta = node.metadata as
      (DatabaseObjectNodeMetadata & { connectionId?: string })
      | undefined;
    const routineConnectionId =
      routineMeta?.connectionId ?? routineMeta?.objectDetails?.connectionId;
    const routineEngine = routineConnectionId
      ? getEngineForConnection(routineConnectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateRoutineContextMenuActions(
      effectiveType as 'stored-procedure' | 'function',
      routineEngine,
    );
  } else if (effectiveType === 'database') {
    const engine = node.metadata?.engine as string | undefined;
    nodeWithActions.contextMenuActions = generateDatabaseContextMenuActions(engine);
  } else if (effectiveType === 'schema-container-folder') {
    // Schema-container nodes get a schema-scoped menu (Script new / Script
    // schema as). Must precede the generic `-folder` branch since the type
    // also ends with `-folder`.
    const engine = node.metadata?.engine as string | undefined;
    nodeWithActions.contextMenuActions = generateSchemaContextMenuActions(engine);
  } else if (node.type === 'partition' || node.type === 'partitions-folder') {
    // Must precede the generic `-folder` branch since 'partitions-folder'
    // also ends with `-folder`.
    nodeWithActions.contextMenuActions = generatePartitionContextMenuActions(node.type);
  } else if (effectiveType.endsWith('-folder')) {
    // Move pinned-folder branch ahead of leaf-object branches so a pinned-folder
    // gets the folder context menu (Script New..., Refresh, etc.).
    const engine = node.metadata?.engine as string | undefined;
    nodeWithActions.contextMenuActions = generateActionsForNodeType(effectiveType, engine);
  } else if (node.type === 'key') {
    const keyMeta = node.metadata as KeyNodeMetadata | undefined;
    const keyEngine = keyMeta?.connectionId
      ? getEngineForConnection(keyMeta.connectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateKeyContextMenuActions(keyEngine);
  } else if (node.type === 'constraint') {
    const constraintMeta = node.metadata as ConstraintNodeMetadata | undefined;
    const constraintEngine = constraintMeta?.connectionId
      ? getEngineForConnection(constraintMeta.connectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateConstraintContextMenuActions(constraintEngine);
  } else if (node.type === 'index') {
    nodeWithActions.contextMenuActions = generateIndexContextMenuActions();
  } else if (node.type === 'trigger') {
    const triggerMeta = node.metadata as TriggerNodeMetadata | undefined;
    const triggerEngine = triggerMeta?.connectionId
      ? getEngineForConnection(triggerMeta.connectionId)
      : undefined;
    nodeWithActions.contextMenuActions = generateTriggerContextMenuActions(triggerMeta?.isEnabled ?? true, triggerEngine);
  } else if (node.type === 'statistic') {
    nodeWithActions.contextMenuActions = generateStatisticContextMenuActions();
  } else if (node.type === 'directory') {
    nodeWithActions.contextMenuActions = generateDirectoryContextMenuActions();
  } else if (isSecurityObjectType(node.type)) {
    nodeWithActions.contextMenuActions = generateSecurityObjectContextMenuActions(node.type);
  } else if (node.type === 'connection') {
    // Generate connection-specific actions (Import .bacpac, Disconnect, Refresh)
    const engine = node.metadata?.engine as string | undefined;
    nodeWithActions.contextMenuActions = generateConnectionContextMenuActions(engine);
  } else if (node.type === 'dashboard') {
    // S23 — Open / Rename / Duplicate / Delete. Duplicate is preemptively
    // blocked (disabled + tooltip) at the free-tier cap; the repository is
    // still the real enforcement point (main-process-only licensing).
    const atFreeCap = !hasProAccess && dashboardCount >= FREE_DASHBOARD_LIMIT;
    nodeWithActions.contextMenuActions = generateDashboardContextMenuActions({
      duplicateBlocked: atFreeCap,
    });
  } else if (node.type === 'dashboards-root') {
    // No menu on the root itself in this slice — only its children do.
    nodeWithActions.contextMenuActions = [];
  } else if (node.type === 'pinned-workspace') {
    // Task 16 — `pinned-workspace` has no canonical node to unwrap to (unlike
    // pinned-database/-folder/-object), so it gets its own dedicated menu.
    // `getUpdateFromTabAvailability` reads a fresh `useWorkspaceStore`
    // snapshot (no subscription) — right-click always selects the row first
    // (`TreeNodeContent`'s `onContextMenu`), which re-renders this component
    // just before the menu opens, so the availability check is fresh enough
    // without paying for a store subscription on every tree row.
    const wsNode = node as unknown as PinnedWorkspaceNode;
    const wsConnectionId = wsNode.metadata?.connectionId;
    nodeWithActions.contextMenuActions = wsConnectionId
      ? generatePinnedWorkspaceContextMenuActions(
          getUpdateFromTabAvailability(wsConnectionId, wsNode.pinRef),
        )
      : [];
  } else if (!node.contextMenuActions || node.contextMenuActions.length === 0) {
    if (node.type !== 'loading' && node.type !== 'message' && node.type !== 'error') {
      const fallbackActions: import('../../models/TreeModel').ContextMenuAction[] = [];
      // Add "Copy name" for non-folder leaf objects (e.g. columns)
      if (!node.type.endsWith('-folder')) {
        const typeLabel = node.type.replace(/-/g, ' ');
        fallbackActions.push({ id: 'copy-name', label: `Copy ${typeLabel} name`, icon: 'clipboard' });
      }
      if (
        node.canHaveChildren ||
        (node.children && node.children.length > 0)
      ) {
        if (fallbackActions.length > 0) {
          fallbackActions.push({ id: 'separator', label: '', isSeparator: true });
        }
        fallbackActions.push(createRefreshAction());
      }
      if (fallbackActions.length > 0) {
        nodeWithActions.contextMenuActions = fallbackActions;
      }
    }
  }

  const hasContextMenu =
    !!nodeWithActions.contextMenuActions &&
    nodeWithActions.contextMenuActions.length > 0;

  const handleNodeSelect = (e: React.MouseEvent) => {
    e.stopPropagation();

    onNodeSelect(node.id);
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    onNodeSelect(node.id);

    if (showExpander) {
      onNodeExpand(node.id);
    }
  };

  // Node types that have dedicated context menu services handling 'copy-name' with real metadata names
  const typesWithDedicatedCopyName = new Set(['key', 'constraint', 'index', 'trigger', 'statistic']);

  const handleContextMenuAction =
    (actionId: string) => (e: Event) => {
      e.stopPropagation();

      setTimeout(() => {
        if (actionId === 'copy-name' && !typesWithDedicatedCopyName.has(node.type)) {
          void writeClipboardText(node.name);
        } else if (actionId === 'refresh' && onNodeRefresh) {
          onNodeRefresh(nodeWithActions.id);
        } else if (onContextMenuAction) {
          onContextMenuAction(nodeWithActions.id, actionId);
        }
      }, 0);
    };

  const isPreloading =
    node.type === 'connection' &&
    node.metadata?.isLoading &&
    typeof node.metadata?.loadingProgress === 'number';

  // Only use TreeNodeLoading for actual 'loading' type nodes (the child indicator)
  // Parent nodes should render normally and show skeleton children
  if (node.type === 'loading' && !isPreloading) {
    return <TreeNodeLoading node={node} depth={depth} />;
  }

  // Pinned rows sit inside a tinted PinnedAreaShell — keep their wrappers
  // (and any expanded descendants) transparent so the shell tint shows under
  // the whole area, not just the header. Everything else keeps `bg-background`.
  const isPinnedNode =
    node.type === 'pinned-database' ||
    node.type === 'pinned-schema' ||
    node.type === 'pinned-folder' ||
    node.type === 'pinned-object' ||
    node.type === 'pinned-workspace';
  const inPinnedArea = insidePinnedArea || isPinnedNode;
  const wrapperBg = inPinnedArea ? '' : 'bg-background';

  // Make table/view rows draggable so they can be dropped onto the VQE FROM
  // drop target (`SchemaTreeDropTarget` in `FromSection.tsx`). The MIME payload
  // matches `VqeDragTablePayload` consumed by `parseVqeDragTablePayload`.
  const isDraggableNode = !sortable && (effectiveType === 'table' || effectiveType === 'view');
  const handleVqeDragStart = isDraggableNode
    ? (e: React.DragEvent<HTMLDivElement>) => {
        const od = (
          node.metadata as
            | { objectDetails?: { schema?: string; metadata?: Record<string, unknown> } }
            | undefined
        )?.objectDetails;
        const schema = od?.schema || undefined;
        const database =
          extractDatabaseFromParentId(node.parentId) ||
          (od?.metadata?.database as string | undefined) ||
          undefined;
        e.dataTransfer.setData(
          VQE_DRAG_TABLE_MIME,
          JSON.stringify({ name: node.name, schema, database, quoted: false }),
        );
        e.dataTransfer.effectAllowed = 'copy';
      }
    : undefined;

  // S23 — double-click/Enter activation. No such affordance exists elsewhere
  // in the tree (plain row, click-to-select only — Docs/ObjectExplorer.md/G20);
  // 'dashboard' is the first node type to need it, opening the tab directly.
  const handleActivate =
    node.type === 'dashboard'
      ? () => {
          const dashboardId = (node.metadata as { dashboardId?: string } | undefined)?.dashboardId;
          if (dashboardId) openDashboardFromExplorer(dashboardId);
        }
      : isStorageResourceNodeType(node.type) || node.type === 'pinned-storage'
        ? () => {
            // Azure Storage (spec §5.1): double-click / Enter opens the browser tab.
            const storageNode = node as unknown as StorageMenuNode;
            openStorageExplorerTab({
              connectionId: storageNode.metadata.connectionId,
              root: storageNode.metadata.resource,
              source: node.type === 'pinned-storage' ? 'pin' : 'tree',
            });
          }
        : undefined;

  const nodeContent = (
    <TreeNodeContent
      node={node}
      depth={depth}
      isSelected={isSelected}
      isExpanded={!!isExpanded}
      showExpander={showExpander}
      iconMap={iconMap}
      onNodeSelect={handleNodeSelect}
      onActivate={handleActivate}
      onExpandToggle={handleExpandToggle}
      onContextMenu={() => {
        if (!isSelected && onNodeSelect) {
          onNodeSelect(node.id);
        }
        // A1 — recompute a pinned-workspace row's Update availability even when
        // the row is already selected (no selection change to trigger a render).
        if (node.type === 'pinned-workspace') {
          forceContextMenuRefresh();
        }
      }}
      isPreloading={isPreloading}
      isHighlighted={isHighlighted}
      shouldScrollIntoView={shouldScrollIntoView}
      onHighlightComplete={() => {
        // Handle connection highlight completion
        if (isConnectionHighlighted && connectionId) {
          onNodeHighlightComplete?.(connectionId);
        }
        // Handle node highlight completion
        if (isNodeHighlighted) {
          onHighlightedNodeComplete?.();
        }
      }}
      onReconnect={onReconnect}
      sortable={sortable}
      insidePinnedArea={inPinnedArea}
      connectionColor={effectiveConnectionColor}
      draggable={isDraggableNode}
      onDragStart={handleVqeDragStart}
    />
  );

  const content = hasContextMenu ? (
    <TreeNodeContextMenu
      actions={nodeWithActions.contextMenuActions ?? []}
      refreshActions={isStorageNodeType(node.type) || node.type === 'pinned-storage'
        ? () => generateStorageContextMenuActions(node as unknown as StorageMenuNode)
        : undefined}
      getActionHandler={handleContextMenuAction}
    >
      {nodeContent}
    </TreeNodeContextMenu>
  ) : (
    nodeContent
  );

  return (
    <div className={`${wrapperBg} [box-shadow:none] [outline:none]`}>
      {content}

      {isPreloading && (
        <div className="px-2 mt-0">
          <Progress
            className="w-full h-1"
            value={node.metadata?.loadingProgress}
          />
        </div>
      )}

      {isExpanded && (
        <div className={`${wrapperBg} animate-in slide-in-from-top-1 duration-150`}>
          {(() => {
            // Determine if we should show skeleton loading state
            const noRealChildren = !node.children || node.children.length === 0;
            const expectsChildren =
              node.canHaveChildren || node.type.includes('-folder');
            const showSkeleton =
              // Show if node is loading
              isLoading ||
              // Show if children contain loading placeholder
              hasLoadingChildren ||
              // Show if just expanded with no children but expects them
              (noRealChildren && expectsChildren);

            if (showSkeleton) {
              return <TreeNodeSkeleton depth={depth + 1} count={3} />;
            }

            // When the current node is a connection, its first children may
            // be the pinned wrappers (`pinned-database` / `pinned-folder` /
            // `pinned-object`). Wrap ONLY those in a dnd-kit SortableContext so
            // drag-to-reorder is scoped to pinned siblings; everything else
            // (Databases, Security, ...) renders outside the sortable scope.
            if (node.type === 'connection' && node.children && node.children.length > 0) {
              const connectionId = (node.metadata as { id?: string })?.id;
              const pinnedChildren = node.children.filter(
                (c) =>
                  c.type === 'pinned-database' ||
                  c.type === 'pinned-schema' ||
                  c.type === 'pinned-folder' ||
                  c.type === 'pinned-object' ||
                  c.type === 'pinned-workspace',
              );
              const nonPinnedChildren = node.children.filter(
                (c) =>
                  c.type !== 'pinned-database' &&
                  c.type !== 'pinned-schema' &&
                  c.type !== 'pinned-folder' &&
                  c.type !== 'pinned-object' &&
                  c.type !== 'pinned-workspace',
              );

              const databasesFolder = node.children.find((c) => c.type === 'databases-folder');
              const accessibleDatabaseCount = (databasesFolder?.children ?? []).filter(
                (c) => c.type === 'database',
              ).length;

              // ONE shape with or without pins — [pinned slot, keyed children] — so pinning the first object (or
              // unpinning the last) keeps every other row mounted: an array here and a fragment there would make
              // React re-mount the whole subtree, closing an open context menu (A+B merge, cross-over item 12).
              return (
                <>
                  {connectionId && pinnedChildren.length > 0 ? (
                    <PinnedChildrenSortable
                      connectionId={connectionId}
                      pinnedChildren={pinnedChildren}
                      accessibleDatabaseCount={accessibleDatabaseCount}
                      depth={depth + 1}
                      connectionColor={effectiveConnectionColor}
                    />
                  ) : null}
                  {(connectionId && pinnedChildren.length > 0 ? nonPinnedChildren : node.children).map((childNode) => (
                    <TreeNode
                      key={childNode.id}
                      node={childNode}
                      depth={depth + 1}
                      insidePinnedArea={inPinnedArea}
                      connectionColor={effectiveConnectionColor}
                    />
                  ))}
                </>
              );
            }

            return node.children?.map((childNode) => (
              <TreeNode
                key={childNode.id}
                node={childNode}
                depth={depth + 1}
                insidePinnedArea={inPinnedArea}
                connectionColor={effectiveConnectionColor}
              />
            ));
          })()}
        </div>
      )}
    </div>
  );
}

/**
 * Perf — memoized so a selection / highlight change (which now flows through
 * `treeSelectionStore` rather than the `TreeContext` value) re-renders only
 * the rows that actually changed. Every prop this component takes is either a
 * primitive or a `processedNodes` entry whose identity is stable until the
 * tree data itself changes, so the default shallow comparison is the right
 * one — do NOT add an object/array prop without memoizing it at the call site.
 */
export const TreeNode = React.memo(TreeNodeImpl);
TreeNode.displayName = 'TreeNode';

/**
 * `PinnedChildrenSortable` — wraps the pinned-sibling list under a connection
 * node in a `DndContext` + `SortableContext` scoped to ONLY those siblings.
 * Non-pinned children (Databases folder, Security folders, etc.) render
 * outside this context and remain non-draggable.
 *
 * On drop the new order is forwarded to the pinned-objects store, which
 * persists via the `connectionMetaInfo` IPC. If the IPC rejects, the store
 * snapshot is unchanged and the tree re-renders in the previous order — a
 * silent revert (the operation is local & low-stakes; no toast needed).
 */
function PinnedChildrenSortable({
  connectionId,
  pinnedChildren,
  accessibleDatabaseCount,
  depth,
  connectionColor,
}: {
  connectionId: string;
  pinnedChildren: TreeNodeType[];
  accessibleDatabaseCount: number;
  depth: number;
  connectionColor?: string;
}) {
  const { track } = useAnalytics();
  const { onContextMenuAction } = useTreeContext();
  // Right-clicking a database-area header opens the same menu as the canonical
  // database node. Actions dispatch against the database node's deterministic
  // id (the node exists eagerly in the tree once the DB list is loaded), so
  // the existing `handleContextMenuAction` pipeline handles them unchanged.
  const databaseMenuActions = useMemo(
    () => generateDatabaseContextMenuActions(getEngineForConnection(connectionId)),
    [connectionId],
  );
  // Review finding #6 — the `SortableContext items` array AND the reorder
  // payload are BOTH derived from the rendered `pinnedChildren` (which each
  // carry their own `pinRef`). Without this, the store could update one tick
  // before the tree rebuild and `useSortable(id)` would receive an id absent
  // from `items`, silently dropping the drag with no toast.
  const pinnedIds = useMemo(
    () => pinnedChildren.map((c) => c.id),
    [pinnedChildren],
  );

  const runs: { run: PinnedAreaRun; nodes: TreeNodeType[] }[] = useMemo(() => {
    const refs = pinnedChildren
      .map((c) => (c as { pinRef?: PinnedObjectRef }).pinRef)
      .filter((r): r is PinnedObjectRef => r !== undefined);

    const grouped = groupPinnedChildrenByDatabase(refs, { accessibleDatabaseCount });

    const out: { run: PinnedAreaRun; nodes: TreeNodeType[] }[] = [];
    let cursor = 0;
    for (const r of grouped) {
      out.push({ run: r, nodes: pinnedChildren.slice(cursor, cursor + r.refs.length) });
      cursor += r.refs.length;
    }
    return out;
  }, [pinnedChildren, accessibleDatabaseCount]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      if (!e.over) return;
      // Review finding #6 — pure reorder logic + side-effect dispatch were
      // extracted to `pinnedReorderHandler.ts` so they're unit-testable. The
      // e2e for drag is routed around dnd-kit's headless flakiness; the
      // extracted helpers carry the load-bearing coverage instead.
      const ordered = computeReorderedRefs(
        pinnedChildren.map((c) => ({
          id: c.id,
          pinRef: (c as { pinRef?: PinnedObjectRef }).pinRef,
        })),
        String(e.active.id),
        String(e.over.id),
      );
      if (!ordered) return;
      void applyPinnedReorder({
        connectionId,
        ordered,
        store: usePinnedObjectsStore.getState(),
        analytics: { track },
        toast,
      });
    },
    [pinnedChildren, connectionId, track],
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={pinnedIds} strategy={verticalListSortingStrategy}>
        {runs.map(({ run, nodes }, runIdx) => (
          <PinnedAreaShell
            key={`${run.database}-${runIdx}`}
            run={run}
            databaseContextMenu={{
              actions: databaseMenuActions,
              onAction: (actionId) => {
                // Defer like TreeNode.handleContextMenuAction so the menu can
                // close before an action opens a dialog / switches workspace.
                setTimeout(() => {
                  onContextMenuAction(createDatabaseNodeId(connectionId, run.database), actionId);
                }, 0);
              },
            }}
          >
            {nodes.map((childNode) => (
              <SortablePinnedTreeNode
                key={childNode.id}
                node={childNode}
                depth={depth}
                connectionColor={connectionColor}
              />
            ))}
          </PinnedAreaShell>
        ))}
      </SortableContext>
    </DndContext>
  );
}

/**
 * Thin wrapper around `TreeNode` that calls `useSortable` for a pinned row
 * and forwards the resulting sortable wiring through props. The handle markup
 * itself lives in `TreeNodeContent` so the grip sits inline with the row.
 */
function SortablePinnedTreeNode({
  node,
  depth,
  connectionColor,
}: {
  node: TreeNodeType;
  depth: number;
  connectionColor?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <TreeNode
      node={node}
      depth={depth}
      sortable={{ setNodeRef, style, attributes, listeners, isDragging }}
      connectionColor={connectionColor}
    />
  );
}
